@if (authRequired()) {
  <!-- STATUS_PASSWORD is set and the SPA request had no credentials. We
       can't drive the Basic-auth prompt from XHR, so we tell the user
       to load /status as a top-level page (where the browser will
       prompt natively) and then return here. -->
  <div class="status-error">
    <mat-icon>lock</mat-icon>
    <span>
      This status page is password-protected. Open
      <a href="/status" rel="noopener">/status</a> directly in the
      browser to authenticate, then reload this view.
    </span>
  </div>
} @else if (!data() && isStartingUp()) {
  <!-- First few seconds of failed fetches: assume the server is still booting
       (Express not up yet, dev-proxy can't connect, etc.) and show a soft
       "starting up" state instead of a red error. Polling continues underneath. -->
  <div class="status-loading">
    <mat-spinner diameter="20"></mat-spinner>
    <span>Connecting to server…</span>
  </div>
} @else if (error() && !data()) {
  <div class="status-error">
    <mat-icon>error_outline</mat-icon>
    <span>Could not load server status: {{ error() }}</span>
  </div>
} @else if (data(); as vm) {
  <div class="status-page">
    <header class="status-header">
      <div class="status-title">
        <h1>{{ title() }}</h1>
        <span class="status-badge"
              [class.ok]="vm.overall === 'ok'"
              [class.bad]="vm.overall !== 'ok'"
              [matTooltip]="vm.overall === 'ok' ? 'All checks passing' : 'One or more health checks need attention'">
          <mat-icon>{{ vm.overall === 'ok' ? 'check_circle' : 'warning' }}</mat-icon>
          {{ vm.overall === 'ok' ? 'healthy' : 'needs attention' }}
        </span>
      </div>
      <div class="status-meta">
        Running for {{ vm.instance.uptime }}
        @if (vm.instance.version) {
          · build
          <code class="version-sha"
                [matTooltip]="'Full SHA: ' + vm.instance.version + (vm.instance.versionSource === 'git' ? ' (local git checkout — not a CI build)' : '')">{{ vm.instance.version.slice(0, 7) }}</code>
          @if (vm.instance.versionSource === 'git') {
            <span class="version-source-tag" matTooltip="This SHA came from `git rev-parse HEAD`; no CI commit env var was set.">local</span>
          }
        }
        @if (vm.instance.namespace) { · {{ vm.instance.namespace }} }
        <span class="muted" [matTooltip]="'Node.js runtime version (process ID ' + vm.instance.pid + ')'">· node {{ vm.instance.nodeVersion }}</span>
      </div>
      @if (vm.overall !== 'ok' && vm.overallReasons.length) {
        <div class="status-reasons">
          <div class="status-reasons-heading">What needs attention</div>
          <ul class="status-reasons-list">
            @for (reason of vm.overallReasons; track reason) {
              @let explanation = explainReason(reason);
              <li>
                <div class="reason-title">{{ explanation.title }}</div>
                <div class="reason-detail">{{ explanation.detail }}</div>
                <div class="reason-code">technical: <code>{{ reason }}</code></div>
              </li>
            }
          </ul>
        </div>
      }
    </header>

    @if (summary(); as s) {
      <section class="status-summary" [class.ok]="s.healthy" [class.bad]="!s.healthy">
        <div class="status-summary-icon">
          <mat-icon>{{ s.healthy ? 'check_circle' : 'warning' }}</mat-icon>
        </div>
        <div class="status-summary-body">
          <h2 class="status-summary-headline">{{ s.headline }}</h2>
          <p class="status-summary-line">{{ s.currentActivity }}</p>
          <p class="status-summary-line">{{ s.recentActivity }}</p>
        </div>
      </section>
    }

    <div class="status-technical-divider">
      <span class="status-technical-label">Technical details</span>
      <span class="status-technical-hint">Numbers and signals operators use to diagnose issues. Safe to ignore if the summary above looks healthy.</span>
    </div>

    <div class="status-grid">

      <mat-card class="status-card span-2">
        <mat-card-content>
          <h2>Server activity</h2>
          <p class="hint">Live snapshot of what the server is doing right now.</p>

          <section class="kv-section">
            <h3 [matTooltip]="'How long the server takes to acknowledge new requests. High numbers mean it is busy processing other work.'">
              Responsiveness
            </h3>
            <dl class="kv">
              <dt>Typical response</dt>
              <dd>{{ vm.resources.eventLoopP50Ms }} ms <span class="kv-hint">half of requests respond at least this fast</span></dd>
              <dt>Slow case (1%)</dt>
              <dd>{{ vm.resources.eventLoopP99Ms }} ms <span class="kv-hint">99% of requests respond faster than this</span></dd>
              <dt>Peak observed</dt>
              <dd>{{ vm.resources.eventLoopMaxMs }} ms <span class="kv-hint">slowest response in the last sample window</span></dd>
            </dl>
          </section>

          <section class="kv-section">
            <h3 [matTooltip]="'Memory usage. The limit is set by --max-old-space-size or the container memory limit.'">
              Memory
            </h3>
            <div class="memory-chart-wrap">
              <canvas #memoryChart></canvas>
            </div>
            <dl class="kv">
              <dt>App memory</dt>
              <dd>
                {{ vm.resources.heapUsedMb }} MB of {{ vm.resources.heapLimitMb }} MB ({{ vm.resources.heapUsedPct }})
                <span class="kv-hint">JavaScript heap; the percent shows how close to the limit we are</span>
              </dd>
              <dt>Currently reserved</dt>
              <dd>{{ vm.resources.heapTotalMb }} MB <span class="kv-hint">memory the engine has allocated to itself; grows toward the limit on demand</span></dd>
              <dt>Total in RAM</dt>
              <dd>{{ vm.resources.rssMb }} MB <span class="kv-hint">all memory the process holds, including code and buffers</span></dd>
              <dt>Native libraries</dt>
              <dd>{{ vm.resources.externalMb }} MB <span class="kv-hint">memory used by C++ extensions (PDF, image, document parsing)</span></dd>
            </dl>
          </section>

          <section class="kv-section">
            <h3 [matTooltip]="'Engine bookkeeping — usually nothing to worry about unless GC pause time is climbing fast.'">
              Engine activity
            </h3>
            <dl class="kv">
              <dt>Memory cleanups</dt>
              <dd>{{ vm.resources.gcCount }} runs <span class="kv-hint">{{ vm.resources.gcPauseTotalMs }} ms spent in garbage collection so far</span></dd>
              <dt>CPU time used</dt>
              <dd>{{ vm.resources.cpuUserMs }} ms in app, {{ vm.resources.cpuSystemMs }} ms in system <span class="kv-hint">total since startup</span></dd>
            </dl>
          </section>
        </mat-card-content>
      </mat-card>

      <mat-card class="status-card">
        <mat-card-content>
          <h2>AI analysis queue</h2>
          <p class="hint">Caps how many AI analyses run at the same time so heavy uploads don't overload the server.</p>
          <div class="queue-layout">
            <div class="queue-chart">
              <canvas #queueChart></canvas>
              <div class="queue-total">
                <div class="big-num">{{ vm.queue.active }}<span class="den">/{{ vm.queue.concurrency }}</span></div>
                <div class="big-num-label">in use</div>
              </div>
            </div>
            <dl class="kv compact">
              <dt>Max in parallel</dt>
              <dd>{{ vm.queue.concurrency }} <span class="kv-hint">ANALYSIS_QUEUE_CONCURRENCY</span></dd>
              <dt>Running</dt><dd>{{ vm.queue.active }}</dd>
              <dt>Waiting</dt><dd>{{ vm.queue.queued }}</dd>
              <dt>Auto-retries</dt>
              <dd>{{ vm.queue.maxAttempts }} <span class="kv-hint">on transient errors</span></dd>
            </dl>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="status-card">
        <mat-card-content>
          <h2>Document tools</h2>
          <p class="hint">External programs and data the server needs to convert documents.</p>
          <dl class="kv">
            <dt>Pandoc</dt>
            <dd>
              @if (vm.environment.pandoc.available) {
                <span class="ok-text">{{ vm.environment.pandoc.version }}</span>
                <span class="kv-hint">document converter (DOCX, LaTeX, HTML); required for imports</span>
              } @else {
                <span class="bad-text">not available</span>
                <span class="kv-hint">{{ vm.environment.pandoc.error || 'binary missing from PATH' }}</span>
              }
            </dd>
            <dt>Export templates</dt>
            <dd>
              @if (vm.environment.templates.mounted) {
                <span class="ok-text">{{ vm.environment.templates.templateCount }} mounted</span>
                <span class="kv-hint">from {{ vm.environment.templates.path }}</span>
              } @else if (vm.environment.templates.configured) {
                <span class="bad-text">not mounted</span>
                <span class="kv-hint">configured at {{ vm.environment.templates.path }} but {{ vm.environment.templates.error }}</span>
              } @else {
                <span class="muted-text">not configured</span>
                <span class="kv-hint">set TEMPLATE_SOURCE to enable export templates</span>
              }
            </dd>
          </dl>
        </mat-card-content>
      </mat-card>

      <mat-card class="status-card span-2">
        <mat-card-content>
          <h2>External services</h2>
          <p class="hint">Things the server needs to reach over the network or filesystem. Green means we can talk to it; red means a firewall, DNS, or mount problem.</p>
          <div class="reach-list">
            @for (probe of vm.reachability; track trackByProbe($index, probe)) {
              <div class="reach-row" [attr.data-state]="reachabilityState(probe)">
                <span class="dot" [class]="reachabilityState(probe)"></span>
                <div class="reach-main">
                  <div class="reach-label">{{ probe.label }}</div>
                  <div class="reach-url">{{ probe.displayLine }}</div>
                </div>
                <div class="reach-meta">
                  <div>{{ reachabilitySummary(probe) }}</div>
                  <div class="reach-when">checked at {{ probe.lastCheckedAt | date:'shortTime' }}</div>
                </div>
              </div>
            }
          </div>
        </mat-card-content>
      </mat-card>

      @for (window of windows(); track window.windowLabel) {
        <mat-card class="status-card">
          <mat-card-content>
            <h2>Recent activity · last {{ window.windowLabel }}</h2>
            <p class="hint">Document imports and AI analyses completed in this window.</p>

            <div class="activity-layout">
              <div class="activity-chart">
                <canvas #activityChart></canvas>
                <div class="activity-total">
                  <div class="big-num">{{ window.total }}</div>
                  <div class="big-num-label">total</div>
                </div>
              </div>
              <dl class="kv compact">
                <dt><span class="kv-color-dot imports"></span>Document imports</dt>
                <dd>{{ window.imports }} <span class="kv-hint">converted; no AI</span></dd>
                <dt><span class="kv-color-dot ai"></span>AI analyses</dt>
                <dd>{{ window.aiAnalyses }} <span class="kv-hint">through the analysis queue</span></dd>
                <dt>Succeeded</dt><dd>{{ window.succeeded }}</dd>
                <dt>Failed</dt><dd>{{ window.failed }}</dd>
                <dt>Failure rate</dt><dd>{{ formatPercent(window.errorRate) }}</dd>
                <dt>Auto-recovered</dt>
                <dd>{{ window.retryableFailures }} <span class="kv-hint">retried by queue</span></dd>
                <dt>Avg duration</dt><dd>{{ window.avgRunSeconds }} s</dd>
                <dt>Avg wait</dt><dd>{{ window.avgQueueWaitSeconds }} s</dd>
              </dl>
            </div>

            @if (window.topFailureStages.length) {
              <h3 class="sub">Where failures happen</h3>
              <table class="status-table">
                <thead><tr><th>Step</th><th class="num">Count</th></tr></thead>
                <tbody>
                  @for (row of window.topFailureStages; track row.stage) {
                    <tr><td>{{ row.stage }}</td><td class="num">{{ row.count }}</td></tr>
                  }
                </tbody>
              </table>
            }
          </mat-card-content>
        </mat-card>
      }

      <mat-card class="status-card span-2">
        <mat-card-content>
          <h2>How long each step takes</h2>
          <p class="hint">Per-step timing for AI analyses in the last 24 hours. <strong>Typical</strong> is the median across runs; <strong>slow case</strong> is the 95th percentile (only 5% of runs are slower).</p>
          @if (vm.stats.last24h.byStageP50Ms.length === 0) {
            <p class="empty">No completed analyses yet. The chart will appear here after the first AI analysis finishes.</p>
          } @else {
            <div class="stage-chart-wrap" [style.height.px]="stageChartHeightPx()">
              <canvas #stageLatencyChart></canvas>
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>

    <footer class="status-footer">
      Updated at {{ vm.generatedAt | date:'mediumTime' }}
      · refreshes every 5 seconds
      · counts reset when the server restarts
    </footer>
  </div>
}
